import { Ankh } from "@/components/guide-public/guide-ornaments";
import { guidePhotoAt, guidePhotoSrcSet } from "@/lib/virtualspace/guide/guide-photos";
import type { GuideLang } from "@/lib/virtualspace/guide/guide-public-shape";

/**
 * A section heading: eyebrow (with the section numeral), the title, and its
 * twin in the other language, which is the Guide's bilingual signature.
 */
export function SectionHead({
  id,
  numeral,
  eyebrow,
  title,
  twin,
  lang,
  as: Heading = "h2",
}: {
  id: string;
  numeral: string;
  eyebrow: string;
  title: string;
  twin: string;
  lang: GuideLang;
  /** h1 on a page whose title this is. */
  as?: "h1" | "h2";
}) {
  const twinLang = lang === "ar" ? "en" : "ar";
  return (
    <div className="eg-section-head">
      <p className="eg-eyebrow">
        {numeral} · {eyebrow}
      </p>
      <div className="eg-section-head__titles">
        <Heading id={id}>{title}</Heading>
        <span className="eg-section-head__twin" lang={twinLang} dir={twinLang === "ar" ? "rtl" : "ltr"}>
          {twin}
        </span>
      </div>
    </div>
  );
}

/**
 * A venue photo, or a lapis panel with an ankh when the venue has none yet.
 * Plain <img>: a photo in the public bucket is offered at three widths
 * (Supabase resizes it on request; `sizes` tells the browser the slot),
 * files from /public are served as they are, sized by their frame.
 */
export function GuidePhoto({
  src,
  alt,
  label,
  style,
  loading = "lazy",
  sizes = "(min-width: 900px) 33vw, 100vw",
  fetchPriority,
}: {
  src: string | null;
  alt: string;
  label: string;
  style?: React.CSSProperties;
  loading?: "lazy" | "eager";
  /** The slot's width, as the `sizes` attribute wants it. */
  sizes?: string;
  fetchPriority?: "high" | "low" | "auto";
}) {
  if (!src) {
    return (
      <div className="eg-photo-missing" style={style} role="img" aria-label={alt}>
        <span style={{ color: "var(--eg-gold-on-lapis)" }}>
          <Ankh size={44} />
        </span>
        <span className="eg-display" style={{ fontSize: 22 }}>
          {label}
        </span>
      </div>
    );
  }
  const srcSet = guidePhotoSrcSet(src);
  // eslint-disable-next-line @next/next/no-img-element
  return (
    <img
      src={srcSet ? guidePhotoAt(src, 960) : src}
      srcSet={srcSet ?? undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      style={style}
    />
  );
}
